import { useState, useCallback } from 'react';
import { Award, Plus } from 'lucide-react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FieldError } from '@/components/glitch-ui/field-error';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { InlineTable, type InlineTableColumn } from '@/components/glitch-ui/inline-table';

const MAX_SIGNAL_LENGTH = 30;

interface TrustSignal {
  id: string;
  name: string;
}

interface BusinessTrustSignalViewProps {
  signals: TrustSignal[];
  setSignals: (signals: TrustSignal[]) => void;
  title?: string;
  description?: string;
}

export const BusinessTrustSignalView: React.FC<BusinessTrustSignalViewProps> = ({
  signals,
  setSignals,
  title = 'Trust Signals',
  description = 'Certifications, awards, media mentions, partnership badges, customer counts, and other credibility indicators',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingSignal, setDeletingSignal] = useState<TrustSignal | null>(null);
  const [newSignalText, setNewSignalText] = useState('');
  const [signalError, setSignalError] = useState<string | undefined>();

  // Tell-on-click validation: Add stays enabled; clicking it marks the missing
  // field instead of sitting silently disabled.
  const handleAdd = useCallback(() => {
    if (!newSignalText.trim()) {
      setSignalError('Required');
      return;
    }

    const entries = newSignalText
      .split(/\n|,(?=\s*[^0-9])/)
      .map(entry => entry.trim())
      .filter(entry => entry.length > 0);

    const newSignals: TrustSignal[] = entries.map(entry => ({
      id: crypto.randomUUID(),
      name: entry,
    }));

    setSignals([...signals, ...newSignals]);
    setNewSignalText('');
    setIsModalOpen(false);
  }, [signals, setSignals, newSignalText]);

  const handleDeleteConfirm = () => {
    if (!deletingSignal) return;
    setSignals(signals.filter((s) => s.id !== deletingSignal.id));
    setDeletingSignal(null);
  };

  const handleUpdateSignal = useCallback((id: string, newName: string) => {
    const currentSignal = signals.find(s => s.id === id);
    if (!currentSignal) return;

    if (newName.length <= MAX_SIGNAL_LENGTH || newName.length < currentSignal.name.length) {
      setSignals(signals.map(s => s.id === id ? { ...s, name: newName } : s));
    }
  }, [signals, setSignals]);

  const openAddModal = () => {
    setNewSignalText('');
    setSignalError(undefined);
    setIsModalOpen(true);
  };

  const columns: InlineTableColumn<TrustSignal>[] = [
    {
      id: 'signal',
      header: 'Signal',
      sortable: true,
      sortFn: (a, b) => a.name.localeCompare(b.name),
      render: (signal) => {
        const isOverLimit = signal.name.length > MAX_SIGNAL_LENGTH;
        return (
          <div className='flex items-center gap-3'>
            <Award className='h-4 w-4 text-gray-500 flex-shrink-0' />
            <div className='flex-1 relative'>
              <Input
                value={signal.name}
                onChange={(e) => handleUpdateSignal(signal.id, e.target.value)}
                className={`w-full bg-transparent border-transparent shadow-none pr-14 group-hover:bg-white group-hover:border-input group-hover:shadow-sm focus:bg-white focus:border-input focus:shadow-sm ${isOverLimit ? 'border-red-300 focus:border-red-500' : ''}`}
                placeholder='e.g. ISO 27001 Certified'
              />
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${isOverLimit ? 'text-red-500' : 'text-gray-400'}`}>
                {signal.name.length}/{MAX_SIGNAL_LENGTH}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      className: 'w-12 px-3 py-3',
      render: (signal) => (
        <button
          onClick={() => setDeletingSignal(signal)}
          className='p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer'
        >
          <Trash2 className='h-4 w-4' />
        </button>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className='flex items-start justify-between gap-3 mb-4'>
        <div className='min-w-0 flex-1'>
          <h3 className='text-lg font-semibold text-gray-900'>{title}</h3>
          <p className='text-sm text-gray-700 mt-1'>{description}</p>
        </div>
        <Button
          variant='outline'
          className='text-gray-900 flex-shrink-0'
          onClick={openAddModal}
        >
          <Plus className='h-4 w-4 mr-2' />
          Add
        </Button>
      </div>

      {/* Table */}
      <InlineTable<TrustSignal>
        data={signals}
        columns={columns}
        rowKey={(s) => s.id}
        emptyIcon={Award}
        emptyTitle='No Trust Signals'
        emptyDescription='Add certifications, awards, or social proof to build credibility.'
      />

      {/* Add Trust Signal Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { setIsModalOpen(open); setSignalError(undefined); }}>
        <DialogContent className='sm:max-w-[500px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Trust Signals</DialogTitle>
            <p className='text-sm text-gray-700 mt-1'>Certifications, awards, media mentions, and other credibility indicators</p>
          </DialogHeader>

          <div className='py-4'>
            <Textarea
              value={newSignalText}
              onChange={(e) => { setNewSignalText(e.target.value); setSignalError(undefined); }}
              placeholder={`ISO 9001 Certified\n500+ Happy Customers\nFeatured in TechCrunch`}
              rows={5}
              className='resize-none'
              error={!!signalError}
            />
            <div className='flex items-center justify-between mt-2'>
              <p className='text-xs text-gray-500'>
                Separate multiple signals with commas or line breaks
              </p>
              <FieldError message={signalError} />
            </div>
          </div>

          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              className='bg-[#290BD9] hover:bg-[#2309C5]'
            >
              Add Trust
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deletingSignal}
        onClose={() => setDeletingSignal(null)}
        onConfirm={handleDeleteConfirm}
        title="Remove Trust Signal"
        description={`Are you sure you want to remove "${deletingSignal?.name || ''}"? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />
    </div>
  );
};
